import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { setAnalyticsContext, track } from './track';
import { EVENT_CATALOG } from './events';
import { isKnownAppName } from '../apps/appNames';

/** Apps are mounted at `/main/<hospital>/<app>/...`. */
const APP_PATH_RE = /^\/main\/[^/]+\/([^/]+)/;

/**
 * The app the visitor is looking at, or null if this path is not an app screen.
 *
 * The slug is checked against the registry rather than taken from the URL as-is:
 * any second segment matches the pattern, but only a registered one renders an
 * app. Without the check a typo'd or retired link — which shows "app not found"
 * — would still land in the stats table as a visit under its raw slug.
 *
 * Exported for tests.
 */
export const appNameFromPath = (pathname: string): string | null => {
  const match = typeof pathname === 'string' ? pathname.match(APP_PATH_RE) : null;
  if (!match) return null;
  let slug: string;
  try {
    slug = decodeURIComponent(match[1]);
  } catch {
    // A malformed escape sequence. Not a path any app is mounted at.
    return null;
  }
  return isKnownAppName(slug) ? slug : null;
};

/**
 * Mounted once at the App root. Fires `app_view` when the visitor ENTERS an
 * app, which is what the app-usage stats count as a visit.
 *
 * "Enters" means the app slug changed. Navigating between screens inside one
 * app is not a new visit, so the ref below holds the current app and only a
 * genuine change re-fires. Leaving the app clears the ref, so coming back — even
 * seconds later — is correctly a second visit. The ref also absorbs React
 * StrictMode's double effect invocation in development, since both runs see the
 * same ref on the same component instance.
 *
 * Hospital and department are resolved server-side from the visitor's own role
 * assignments, so nothing about the user is sent from here.
 */
export const useAppViewTracking = (): void => {
  const location = useLocation();
  const lastAppRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      const appName = appNameFromPath(location.pathname);
      setAnalyticsContext({ appName });

      if (!appName) {
        lastAppRef.current = null;
        return;
      }
      if (lastAppRef.current === appName) return;
      lastAppRef.current = appName;

      track(EVENT_CATALOG.app_view);
    } catch {
      // swallow — analytics must never affect the app
    }
  }, [location.pathname]);
};
